import { type ErrorBody, ErrorBodySchema } from '@studio/contract';

/**
 * Thrown by the query hooks when the backend returns a non-2xx response.
 * The `body` is the parsed `ErrorBody` from the wire (validated against
 * `@studio/contract`'s `ErrorBodySchema`); falls back to a synthesized
 * `INTERNAL_ERROR` if the body doesn't match.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: ErrorBody;

  constructor(status: number, body: ErrorBody) {
    super(`API error ${status}: ${body.code} — ${body.message}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Reads the response body and converts it to an `ApiError`. Always returns
 * an `ApiError`; never throws (caller throws the returned error).
 */
export async function parseApiError(res: Response): Promise<ApiError> {
  let body: ErrorBody;
  try {
    const json = (await res.json()) as unknown;
    body = ErrorBodySchema.parse(json);
  } catch {
    body = {
      code: 'INTERNAL_ERROR',
      message: `Unexpected ${res.status} response`,
    };
  }
  return new ApiError(res.status, body);
}
